import { type ComponentType, type ReactNode } from 'react';
import { getPreviewEditorConfig } from '@/config/editor-registry';
import { getPreviewContainerClass } from '@/lib/asset-preview-utils';

interface AssetPreviewProps {
  asset: {
    uuid: string;
    name: string;
    type: string;
  } | null;
  assetId?: string;
  className?: string;
  children?: ReactNode;
}

export const AssetPreview = ({ asset, assetId, className = '', children }: AssetPreviewProps) => {
  if (!asset || !assetId) {
    return null;
  }

  const previewConfig = getPreviewEditorConfig(asset.type);

  if (!previewConfig) {
    return (
      <div className="text-center py-12">
        <p>Asset type "{asset.type}" preview not supported yet.</p>
        <p className="text-sm text-gray-500 mt-2">Name: {asset.name}</p>
        <p className="text-sm text-gray-500">Type: {asset.type}</p>
      </div>
    );
  }

  const resolvedProps = previewConfig.getProps?.({
    assetId,
    assetType: asset.type,
  });

  const additionalProps = (
    resolvedProps && typeof resolvedProps === 'object'
      ? Object.fromEntries(
          Object.entries(resolvedProps).filter(([key]) => key !== 'assetId')
        )
      : {}
  ) as Record<string, unknown>;

  const PreviewComponent = previewConfig.component as ComponentType<{
    assetId: string;
  } & Record<string, unknown>>;

  return (
    <div className={`${getPreviewContainerClass(asset.type)} ${className}`}>
      <PreviewComponent assetId={assetId} {...additionalProps} />
      {children}
    </div>
  );
};